// service/crm/hooks/useSetContactImportOwnerFallbackMutation.ts

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  contactImportApi,
  contactImportQueryKeys,
} from "service/crm/api/contactImport";
import {
  ContactImportOwnerFallbackAction,
  ContactImportOwnerMapping,
  ContactImportRecord,
} from "types/crm/contactImport";

interface OwnerFallbackVariables {
  importUuid: string;
  action: ContactImportOwnerFallbackAction;
}

/**
 * Settles the Owner decision for every row the import could not resolve.
 *
 * The saved import replaces what is cached, since the call resets the job and
 * the screens after this one read that state back.
 */
export const useSetContactImportOwnerFallbackMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<ContactImportRecord, unknown, OwnerFallbackVariables>({
    mutationFn: ({ importUuid, action }) =>
      contactImportApi.setOwnerFallback(importUuid, { action }),
    onSuccess: (record, variables) => {
      const targetUuid = record?.uuid || variables.importUuid;
      if (targetUuid) {
        // Prefix match: the page size in the key is not this call's business.
        queryClient.setQueriesData<ContactImportRecord>(
          { queryKey: contactImportQueryKeys.detail(targetUuid) },
          (old) => {
            if (!old) return record;
            return {
              ...old,
              ...record,
              uuid: old.uuid ?? record?.uuid ?? targetUuid,
              ownerMapping: {
                ...old.ownerMapping,
                ...record?.ownerMapping,
                resolution: record?.ownerMapping?.resolution ?? {
                  action: variables.action,
                },
              } as ContactImportOwnerMapping,
            };
          }
        );
      }
    },
  });
};
